import { e, getDueDateUrgency, normalizeStatus } from './modules/utils.js';
import { PRESET_COLORS, DEFAULT_SUB_COLUMNS } from './modules/state.js';
import { loadOverrides, saveOverrides, cloudSync } from './modules/storage.js';
import { getAuthHeader } from './modules/auth-client.js';
import { softLogout } from './modules/auth-client.js';
import { boardData, setBoardData, setAiName, AI_NAME, navStack } from './core.js';

// ── Load board from API ──
export async function loadBoard() {
  try {
    const res = await fetch('/api/board', { headers: getAuthHeader() });
    if (res.status === 401) { softLogout(); return; }
    if (!res.ok) throw new Error(`Board fetch failed: ${res.status}`);
    const _boardJson = await res.json();
    if (_boardJson.aiName) setAiName(_boardJson.aiName);
    setBoardData(migrateSchema(_boardJson));
    // renderFromState is called by the caller
    const { renderFromState } = await import('./render.js');
    renderFromState();
    cloudSync();
  } catch (err) {
    console.error('Failed to load board data:', err);
    document.getElementById('board').innerHTML =
      `<p style="padding:40px;color:var(--danger);">Failed to load board data: ${e(err.message || err)}. Try refreshing (Cmd+Shift+R).</p>`;
  }
}

// Compute latest activity timestamp for a card (used for amber dot expiry)
export function getCardLastActivity(cardId) {
  const ov = loadOverrides();
  let latest = 0;
  // Check task completions for this card
  if (boardData) {
    const allCards = [];
    function collectCards(cards) { cards.forEach(c => { allCards.push(c); if (c.children) collectCards(c.children); }); }
    (boardData.columns || []).forEach(col => collectCards(col.projects || []));
    const card = allCards.find(c => c.id === cardId);
    if (card && card.tasks) {
      card.tasks.forEach(t => {
        const completedAt = ov.taskCompletedAt?.[t.id];
        if (completedAt) latest = Math.max(latest, new Date(completedAt).getTime());
      });
    }
    // Check added tasks
    const added = ov.addedTasks?.[cardId];
    if (added && added.length) {
      added.forEach(t => {
        if (t.addedAt) latest = Math.max(latest, new Date(t.addedAt).getTime());
        const completedAt = ov.taskCompletedAt?.[t.id];
        if (completedAt) latest = Math.max(latest, new Date(completedAt).getTime());
      });
    }
    // Check status changes
    const statusOverride = ov.cardStatusOverrides?.[cardId];
    if (statusOverride?.doneAt) latest = Math.max(latest, new Date(statusOverride.doneAt).getTime());
  }
  // Check overrides save time as a fallback
  if (ov._savedAt && latest === 0) latest = ov._savedAt;
  return latest;
}

// ── Schema migration (client-side) ──
export function migrateSchema(data) {
  if (!data) return data;
  if (!data.schemaVersion) data.schemaVersion = 1;
  data.columns = (data.columns || []).map(col => ({
    archived: false,
    ...col,
    projects: (col.projects || []).map(ensureCardDefaults),
  }));
  return data;
}

export function ensureCardDefaults(card) {
  const out = {
    id: card.id || ('card-' + Date.now() + '-' + Math.random().toString(36).slice(2)),
    name: card.name || 'Untitled',
    color: card.color || PRESET_COLORS[0],
    createdAt: card.createdAt || new Date().toISOString().split('T')[0],
    dueDate: card.dueDate !== undefined ? card.dueDate : null,
    endDate: card.endDate !== undefined ? card.endDate : null,
    status: card.status || '',
    outcome: card.outcome || '',
    archived: card.archived || false,
    tasks: (card.tasks || []).map(t => ({
      id: t.id || ('task-' + Date.now() + '-' + Math.random().toString(36).slice(2)),
      text: t.text || '',
      done: !!t.done,
      dueDate: t.dueDate || null,
      completedAt: t.completedAt || null,
    })),
    contacts: card.contacts || [],
    comms: card.comms || [],
  };
  if (Array.isArray(card.children)) {
    out.children = card.children.map(ensureCardDefaults);
    out.subBoardColumns = card.subBoardColumns;
    out.columnId = card.columnId;
  } else if (card.columnId !== undefined) {
    out.columnId = card.columnId;
  }
  // Preserve any future unknown fields (forward compat)
  for (const k of Object.keys(card)) {
    if (!(k in out)) out[k] = card[k];
  }
  return out;
}

// ── Tree helpers ──
export function findCardById(id, data) {
  for (const col of data.columns) {
    for (const proj of col.projects) {
      const found = findCardInTree(id, proj);
      if (found) return found;
    }
  }
  return null;
}

export function findCardInTree(id, card) {
  if (card.id === id) return card;
  if (card.children) {
    for (const child of card.children) {
      const found = findCardInTree(id, child);
      if (found) return found;
    }
  }
  return null;
}

// ── Current view ──
export function getCurrentView(data) {
  if (navStack.length === 0) {
    return { columns: data.columns, parentCard: null };
  }
  let currentCard = null;
  for (let i = 0; i < navStack.length; i++) {
    const targetId = navStack[i].id;
    if (i === 0) {
      currentCard = findCardById(targetId, data);
    } else {
      if (currentCard && currentCard.children) {
        currentCard = currentCard.children.find(c => c.id === targetId) || null;
      }
    }
    if (!currentCard) return { columns: [], parentCard: null };
  }
  if (!currentCard) return { columns: [], parentCard: null };

  const subColumns = currentCard.subBoardColumns || DEFAULT_SUB_COLUMNS;
  const children = currentCard.children || [];
  const columns = subColumns.map(sc => ({
    id: sc.id,
    name: (sc.emoji || '') + ' ' + sc.title,
    projects: children.filter(c => c.columnId === sc.id)
  }));
  return { columns, parentCard: currentCard };
}

// ── Apply overrides recursively ──
export function applyOverridesRecursive(card, ov) {
  // Name override
  if (ov.cardNames && card.id in ov.cardNames) {
    card.name = ov.cardNames[card.id];
  }
  // Due date override
  if (ov.cardDueDates && card.id in ov.cardDueDates) {
    card.dueDate = ov.cardDueDates[card.id];
  }
  if (ov.cardEndDates && card.id in ov.cardEndDates) {
    card.endDate = ov.cardEndDates[card.id];
  }
  // Status override
  if (ov.cardStatus && card.id in ov.cardStatus) {
    card.status = normalizeStatus(ov.cardStatus[card.id]);
  } else if (card.status) {
    card.status = normalizeStatus(card.status);
  }
  // Contacts override
  if (ov.cardContacts && card.id in ov.cardContacts) {
    card.contacts = ov.cardContacts[card.id];
  }
  // Convert leaf to parent (nest action)
  if (ov.convertedToParent && ov.convertedToParent[card.id] && !card.children) {
    card.children = [];
    card.subBoardColumns = ov.convertedToParent[card.id].subBoardColumns;
  }
  // Added tasks first — so taskStates applies to them too
  if (ov.addedTasks && ov.addedTasks[card.id]) {
    if (!card.tasks) card.tasks = [];
    for (const task of ov.addedTasks[card.id]) {
      if (!card.tasks.find(t => t.id === task.id)) {
        card.tasks.push({ ...task });
      }
    }
  }
  // Task states — now covers both board tasks and user-added tasks
  if (card.tasks && ov.taskStates) {
    for (const task of card.tasks) {
      if (task.id in ov.taskStates) task.done = ov.taskStates[task.id];
    }
  }
  // Task done overrides (from delegate approve)
  if (card.tasks && ov.taskDoneOverrides) {
    for (const task of card.tasks) {
      if (ov.taskDoneOverrides[task.id]) task.done = true;
    }
  }
  // Task text edits
  if (card.tasks && ov.taskTexts) {
    for (const task of card.tasks) {
      if (ov.taskTexts[task.id]) task.text = ov.taskTexts[task.id];
    }
  }
  // Task completion dates
  if (card.tasks && ov.taskCompletedAt) {
    for (const task of card.tasks) {
      if (ov.taskCompletedAt[task.id]) task.completedAt = ov.taskCompletedAt[task.id];
    }
  }
  // Task due dates
  if (card.tasks && ov.taskDueDates) {
    for (const task of card.tasks) {
      if (ov.taskDueDates[task.id]) task.dueDate = ov.taskDueDates[task.id];
    }
  }
  // Task next-action flags (Day 5)
  if (card.tasks && ov.taskNextActions) {
    for (const task of card.tasks) {
      task.nextAction = !!ov.taskNextActions[task.id];
    }
  }
  // Task owners (Day 6)
  if (card.tasks && ov.taskOwners) {
    for (const task of card.tasks) {
      if (ov.taskOwners[task.id]) task.owner = ov.taskOwners[task.id];
    }
  }
  // Ripe flag
  if (card.tasks && ov.taskRipe) {
    for (const task of card.tasks) {
      if (task.id in ov.taskRipe) task.ripe = ov.taskRipe[task.id];
    }
  }
  // Ripe snoozed date (left-swipe "not today")
  if (card.tasks && ov.taskRipeSnoozed) {
    for (const task of card.tasks) {
      if (ov.taskRipeSnoozed[task.id]) task.ripeSnoozedDate = ov.taskRipeSnoozed[task.id];
    }
  }
  // Card outcome override (Day 7)
  if (ov.cardOutcomes && card.id in ov.cardOutcomes) {
    card.outcome = ov.cardOutcomes[card.id];
  }
  // Card waiting-for override (Day 8)
  if (ov.cardWaitingFor && card.id in ov.cardWaitingFor) {
    card.waitingFor = ov.cardWaitingFor[card.id];
  }
  // Deleted tasks
  if (card.tasks && ov.deletedTaskIds && ov.deletedTaskIds.length > 0) {
    const delSet = new Set(ov.deletedTaskIds);
    card.tasks = card.tasks.filter(t => !delSet.has(t.id));
  }
  // Task order
  if (card.tasks && ov.taskOrder && ov.taskOrder[card.id]) {
    const orderIds = ov.taskOrder[card.id];
    const taskMap = {};
    card.tasks.forEach(t => taskMap[t.id] = t);
    const ordered = [];
    for (const tid of orderIds) {
      if (taskMap[tid]) { ordered.push(taskMap[tid]); delete taskMap[tid]; }
    }
    Object.values(taskMap).forEach(t => ordered.push(t));
    card.tasks = ordered;
  }
  // Children
  if (card.children) {
    if (ov.addedChildren && ov.addedChildren[card.id]) {
      for (const child of ov.addedChildren[card.id]) {
        if (!card.children.find(c => c.id === child.id)) {
          card.children.push(JSON.parse(JSON.stringify(child)));
        }
      }
    }
    if (ov.deletedProjects && ov.deletedProjects.length > 0) {
      const deleted = new Set(ov.deletedProjects);
      card.children = card.children.filter(c => !deleted.has(c.id));
    }
    for (const child of card.children) {
      applyOverridesRecursive(child, ov);
    }
    const subColumns = card.subBoardColumns || DEFAULT_SUB_COLUMNS;
    if (ov.columnOrder) {
      const allChildren = {};
      for (const child of card.children) allChildren[child.id] = child;
      for (const sc of subColumns) {
        if (ov.columnOrder[sc.id]) {
          for (const cid of ov.columnOrder[sc.id]) {
            if (allChildren[cid]) allChildren[cid].columnId = sc.id;
          }
        }
      }
    }
  }
}

// ── Merge overrides into board data ──
export function mergeOverrides(data) {
  const ov = loadOverrides();
  const merged = JSON.parse(JSON.stringify(data));

  // ── User-added columns must be injected FIRST so addedProjects can find them ──
  if (ov.addedColumns) {
    for (const ac of ov.addedColumns) {
      if (!merged.columns.find(c => c.id === ac.id)) {
        merged.columns.push({ id: ac.id, name: ac.name, archived: false, projects: [] });
      }
    }
  }

  if (ov.addedProjects) {
    for (const proj of ov.addedProjects) {
      const col = merged.columns.find(c => c.id === proj.columnId);
      if (col) {
        const newProj = {
          id: proj.id, name: proj.name, color: proj.color,
          createdAt: proj.createdAt, dueDate: proj.dueDate || null, endDate: proj.endDate || null,
          status: proj.status || '', contacts: proj.contacts || [], comms: proj.comms || []
        };
        if (proj.children) {
          newProj.children = proj.children;
          newProj.subBoardColumns = proj.subBoardColumns || DEFAULT_SUB_COLUMNS;
        } else {
          newProj.tasks = proj.tasks || [];
        }
        col.projects.unshift(newProj);
      }
    }
  }

  // Convert leaf cards to parent (nest action) — must happen before addedChildren
  if (ov.convertedToParent) {
    for (const cardId in ov.convertedToParent) {
      const card = findCardById(cardId, merged);
      if (card && !card.children) {
        card.children = [];
        card.subBoardColumns = ov.convertedToParent[cardId].subBoardColumns;
      }
    }
  }

  if (ov.addedChildren) {
    for (const parentId in ov.addedChildren) {
      const parent = findCardById(parentId, merged);
      if (parent && parent.children) {
        for (const child of ov.addedChildren[parentId]) {
          if (!parent.children.find(c => c.id === child.id)) {
            parent.children.push(JSON.parse(JSON.stringify(child)));
          }
        }
      }
    }
  }

  if (ov.deletedProjects && ov.deletedProjects.length > 0) {
    const deleted = new Set(ov.deletedProjects);
    for (const col of merged.columns) {
      col.projects = col.projects.filter(p => !deleted.has(p.id));
    }
    function removeDeletedRecursive(cards) {
      for (const card of cards) {
        if (card.children) {
          card.children = card.children.filter(c => !deleted.has(c.id));
          removeDeletedRecursive(card.children);
        }
      }
    }
    for (const col of merged.columns) removeDeletedRecursive(col.projects);
  }

  for (const col of merged.columns) {
    for (const proj of col.projects) {
      applyOverridesRecursive(proj, ov);
    }
  }

  if (ov.columnOrder) {
    const allProjects = {};
    for (const col of merged.columns) {
      for (const proj of col.projects) allProjects[proj.id] = proj;
    }
    const placed = new Set();
    for (const col of merged.columns) {
      if (ov.columnOrder[col.id]) {
        const orderIds = ov.columnOrder[col.id];
        const ordered = [];
        for (const pid of orderIds) {
          if (allProjects[pid]) { ordered.push(allProjects[pid]); placed.add(pid); }
        }
        // Append any newly-added cards not yet in the saved order (e.g. just created)
        for (const proj of col.projects) {
          if (!placed.has(proj.id)) { ordered.unshift(proj); placed.add(proj.id); }
        }
        col.projects = ordered;
      }
    }
    for (const col of merged.columns) {
      if (!ov.columnOrder[col.id]) {
        col.projects = col.projects.filter(p => !placed.has(p.id));
      }
    }
  }

  // ── Column-level overrides ──
  // Renames
  if (ov.columnRenames) {
    for (const col of merged.columns) {
      if (ov.columnRenames[col.id]) col.name = ov.columnRenames[col.id];
    }
  }
  // User-added columns
  if (ov.addedColumns) {
    for (const ac of ov.addedColumns) {
      if (!merged.columns.find(c => c.id === ac.id)) {
        merged.columns.push({ id: ac.id, name: ac.name, archived: false, projects: [] });
      } else {
        // Already present (e.g. was re-added) — just rename if needed
        const col = merged.columns.find(c => c.id === ac.id);
        if (col && !ov.columnRenames?.[ac.id]) col.name = ac.name;
      }
    }
  }
  // Archived columns — filter after all other processing
  if (ov.archivedColumns && ov.archivedColumns.length > 0) {
    const archived = new Set(ov.archivedColumns);
    merged.columns = merged.columns.filter(c => !archived.has(c.id));
  }
  // Root column order
  if (ov.rootColumnOrder && ov.rootColumnOrder.length > 0) {
    const colMap = {};
    for (const col of merged.columns) colMap[col.id] = col;
    const ordered = [];
    for (const id of ov.rootColumnOrder) {
      if (colMap[id]) { ordered.push(colMap[id]); delete colMap[id]; }
    }
    // Append any columns not in the saved order (newly added)
    for (const col of Object.values(colMap)) ordered.push(col);
    merged.columns = ordered;
  }

  return merged;
}

// ── Date helpers ──
export function formatDatePill(proj) {
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  // Event mode: dueDate (start) + endDate both present
  if (proj.dueDate && proj.endDate) {
    const start = new Date(proj.dueDate + "T00:00:00");
    const end   = new Date(proj.endDate + "T00:00:00");
    const today = new Date(); today.setHours(0,0,0,0);

    const sm = monthNames[start.getMonth()];
    const em = monthNames[end.getMonth()];
    const sd = start.getDate();
    const ed = end.getDate();
    const range = sm === em ? `${sm} ${sd}\u2013${ed}` : `${sm} ${sd}\u2013${em} ${ed}`;

    const daysUntil = Math.ceil((start - today) / 86400000);
    let phase;
    if      (daysUntil <= 0)  phase = "\ud83d\udd34";
    else if (daysUntil <= 7)  phase = "\ud83d\udd34";
    else if (daysUntil <= 14) phase = "\ud83d\udfe0";
    else if (daysUntil <= 21) phase = "\ud83d\udfe1";
    else if (daysUntil <= 35) phase = "\ud83d\udfe1";
    else if (daysUntil <= 60) phase = "\ud83d\udd35";
    else                      phase = "\u26aa";

    const isOver = today > end;
    const urgencyClass = isOver ? "overdue" : (daysUntil <= 14 ? "soon" : "later");

    return `<button class="date-pill urgency-${urgencyClass}" data-proj-id="${proj.id}" title="Edit schedule">${range}</button>`;
  }

  // Normal mode: dueDate only — unchanged
  if (!proj.dueDate) return '';
  const due = new Date(proj.dueDate + 'T00:00:00');
  const label = `${monthNames[due.getMonth()]} ${due.getDate()}`;
  const urgency = getDueDateUrgency(proj.dueDate);
  const pillLabel = urgency === 'overdue' ? `Overdue \u00b7 ${label}` : `Due ${label}`;
  return `<button class="date-pill urgency-${urgency}" data-proj-id="${proj.id}" title="Edit due date">${pillLabel}</button>`;
}

export function getCardSchedule(proj) {
  // Manual override wins if both dueDate and endDate are set
  if (proj.dueDate && proj.endDate) {
    return { start: proj.dueDate, end: proj.endDate, derived: false };
  }
  // Manual start only — no range
  if (proj.dueDate && !proj.endDate) {
    return null; // let formatDatePill handle single due date
  }
  // Auto-derive from task due dates
  const tasks = proj.tasks || [];
  const dates = tasks.map(t => t.dueDate).filter(Boolean).sort();
  if (dates.length >= 2) {
    return { start: dates[0], end: dates[dates.length - 1], derived: true };
  }
  if (dates.length === 1) {
    return { start: dates[0], end: dates[0], derived: true };
  }
  return null;
}

export function formatSchedulePill(proj) {
  const sched = getCardSchedule(proj);
  if (!sched) return formatDatePill(proj); // fall back to normal due date pill

  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const start = new Date(sched.start + "T00:00:00");
  const end   = new Date(sched.end   + "T00:00:00");
  const today = new Date(); today.setHours(0,0,0,0);

  const sm = monthNames[start.getMonth()];
  const em = monthNames[end.getMonth()];
  const sd = start.getDate();
  const ed = end.getDate();
  const range = (sched.start === sched.end)
    ? `${sm} ${sd}`
    : (sm === em ? `${sm} ${sd}\u2013${ed}` : `${sm} ${sd}\u2013${em} ${ed}`);

  const daysUntil = Math.ceil((start - today) / 86400000);
  let phase;
  if      (daysUntil <= 0)  phase = "\ud83d\udd34";
  else if (daysUntil <= 7)  phase = "\ud83d\udd34";
  else if (daysUntil <= 14) phase = "\ud83d\udfe0";
  else if (daysUntil <= 21) phase = "\ud83d\udfe1";
  else if (daysUntil <= 35) phase = "\ud83d\udfe1";
  else if (daysUntil <= 60) phase = "\ud83d\udd35";
  else                      phase = "\u26aa";

  const isOver = today > end;
  const urgencyClass = isOver ? "overdue" : (daysUntil <= 14 ? "soon" : "later");

  return `<button class="date-pill urgency-${urgencyClass}" data-proj-id="${proj.id}" title="${sched.derived ? "Auto-derived from tasks \u00b7 click to override" : "Edit schedule"}">${range}</button>`;
}
